// @givmo/org-console — the words of the Team pages and the invite (OM-94, OM-95, OM-96).
//
// THE WORDS' STANDING. Jonathan approved the Team pages as drawn, with every recommendation and the
// round-4 words on their page (D-CPO-77, 2026-10-24 11:10 PDT: "agree with your recs on the team
// page"; the frames, their README and its words table are in the plan's
// `scratchpad/2026-10-24-1029-om96-team-mockup-frames/`). They are byte-identical here, and
// `copy-law.test.ts` pins each one: changing a word changes a ruling. Two compositions are not on
// that page, because no frame drew the state they say (`ownerCan`, `ownerSameAsMember`); they are
// built from the ruled leads and named below.
//
// RETIRED by the same ruling, and spelled nowhere in this package any more, comments included
// (`copy-law.test.ts` holds their absence): the one sentence every level used to share, and the app
// door's pointer to Team access. A Global Admin no longer meets an app's own invite (their Team page
// is Team access in place), so that door's two Team access remedies went with it, D-CPO-73's
// sentence among them.
//
// THE CLASS ENTERS AS DATA (R5): the host's noun and its app names come from its vocab.

import { GLOBAL_ADMIN_LABEL, MEMBER_LABEL, ROLE_OWNER } from './org-tiers';

export const TEAM_COPY = {
  /**
   * The one team page's one name: every app's Team page, and the Admin Console's page that was
   * "Team access" — their sidebar rows, their headings and the links to them, on both consoles
   * (OM-100, D-CPO-78; Jonathan, 2026-10-24: "we should change "Team" to "Admin Team" right? makes
   * it more clear", then "Yes" for the Admin Console's page). It says whose team the page is: the
   * people who run the organization's apps, never its employees or a fundraiser's teams.
   */
  adminTeam: 'Admin Team',
  /** Under an app's level at Member (OM-94): the lead, then the app's Member permissions. */
  memberCan: `As a ${MEMBER_LABEL}, they can:`,
  /** Under an app's level at Owner: the lead, then what Owner adds to Member. */
  ownerAlsoCan: `As an ${ROLE_OWNER}, they can do everything a ${MEMBER_LABEL} can, and also:`,
  /** An app whose Member level carries nothing (Connected AI, frame F2f). */
  memberNothing: (app: string): string => `As a ${MEMBER_LABEL}, they can’t do anything in ${app}.`,
  /**
   * NOT DRAWN: Owner of an app whose Member level carries nothing (Connected AI at Owner). "Everything
   * a Member can" would point at nothing, so the Member lead is said at Owner instead.
   */
  ownerCan: `As an ${ROLE_OWNER}, they can:`,
  /** NOT DRAWN: Owner of an app whose Owner level adds nothing to Member (no catalog does today). */
  ownerSameAsMember: `As an ${ROLE_OWNER}, they can do everything a ${MEMBER_LABEL} can.`,
  /**
   * A module Owner's last line, under their invite, where the organization has another app (F1d):
   * how an Owner gives an app they own, what only a Global Admin can do, and who that is. The last
   * sentence names the organization's Global Admins from a WHOLE roster, and is left out without one.
   */
  whoCanGiveMore: (globalAdmins: readonly string[]): string => {
    const rule = `To give someone an app you own, invite them from that app’s Admin Team page. Only a ${GLOBAL_ADMIN_LABEL} can give several apps at once or make someone a ${GLOBAL_ADMIN_LABEL}.`;
    const [first, second] = globalAdmins;
    if (first === undefined) return rule;
    if (second === undefined) return `${rule} Your ${GLOBAL_ADMIN_LABEL} is ${first}.`;
    return globalAdmins.length === 2
      ? `${rule} Your ${GLOBAL_ADMIN_LABEL}s are ${first} and ${second}.`
      : `${rule} Your ${GLOBAL_ADMIN_LABEL}s include ${first} and ${second}.`;
  },
  /** Under "Which apps they can open", on the organization-wide invite. */
  exactlyWhatYouChoose: 'They’ll get exactly what you choose here, and nothing else.',
  /**
   * The requests section's line: Team access's, and an app's own Team page's in the same words (the
   * app's page used to explain the approver's permission instead).
   */
  requestsLede: (app: string | null): string =>
    `People asking to open ${app ?? 'an app'}. Approving adds it to their access; declining tells them why.`,
  /** The app column's cell, on the team list and the invites, for someone who can't open the app. */
  noAccess: 'No access',
} as const;
